import { useComponentDidMount } from './useComponentDidMount';
import { useComponentWillUnmount } from './useComponentWillUnmount';
import { useState } from 'react';

export interface WindowDimensions {
    width: number;
    height: number;
}

const getWindowDimensions = (): WindowDimensions => {
    const width = Math.max(window.innerWidth, document.documentElement.clientWidth);
    const height = Math.max(window.innerHeight, document.documentElement.clientHeight);
    return {
        width,
        height,
    };
};

/**
 * Use this hook to get window innerWidth and innerHeight.
 * We are using ResizeObserver where possible and only using resize event as a fallback.
 */
export const useWindowDimensions = (): WindowDimensions => {
    const [windowDimensions, setWindowDimensions] = useState<WindowDimensions>(getWindowDimensions);
    const body = document.getElementsByTagName('body')[0];
    let resizeObserver;

    // Use resize observer
    if ('ResizeObserver' in window) {
        resizeObserver = new ResizeObserver(() => {
            handleResize();
        });
    }

    function handleResize() {
        setWindowDimensions(getWindowDimensions());
    }

    useComponentDidMount(() => {
        resizeObserver?.observe(body);
        window.addEventListener('resize', handleResize);
    });

    useComponentWillUnmount(() => {
        resizeObserver?.unobserve(body);
        window.removeEventListener('resize', handleResize);
    });

    return windowDimensions;
};
